import React, { useCallback, useMemo } from 'react';

import { makeStyles } from '@material-ui/core/styles';
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';

import Visits, { NotAllowedKeysForSearch } from '../../subComponents/visits';
import Table, { createColumn, ColumnType } from '../../subComponents/table';
import { PendingFilesStyleV2 } from '../../../styles/pendingFilesStyle';
import { Name as NameMap } from '../../../constants/name.constant';
import { LOADING_ROWS } from '../../../constants/visits.constant';
import ContentPasteIcon from '../../../assets/contentPaste.svg';
import { get as versionSelector } from '../../../utils/versionSelector';

const NO_VISITS_MESSAGE = 'There are no orders in your queue. Please check back later!';

const Colums = [
  createColumn({ key: 'AsyncIcon', label: '', type: ColumnType.jsx, width: 30, hideSort: true }),
  createColumn({ key: 'DateQueueEntered', label: 'Date Entered...', tooltip: 'Date Entered Your Queue', type: ColumnType.date, width: 140 }),
  createColumn({ key: 'FullName', label: 'Patient Name', type: ColumnType.string, width: 200 }),
  createColumn({ key: 'PatientAge', label: 'Age', type: ColumnType.numeric, width: 50 }),
  createColumn({ key: 'ProductName', label: 'Product', type: ColumnType.string, width: 250 }),
  createColumn({ key: 'StoreFrontName', label: 'Storefront', type: ColumnType.string, width: 160 }),
  createColumn({ key: 'State', label: 'State', type: ColumnType.string, width: 50 }),
  createColumn({ key: 'OrderId', label: 'Order ID', type: ColumnType.string }),
  createColumn({ key: 'ShippingMethod', label: 'Shipping...', tooltip: 'Shipping Method', type: ColumnType.string, width: 120 }),
  createColumn({ key: 'DateLastNoteAdded', label: 'Date You Last Added...', tooltip: 'Date You Last Added a Note', type: ColumnType.date, width: 200 }),
  createColumn({ key: 'Notifications', label: 'Notifications', type: ColumnType.jsx, hideSort: true }),
];

const useStyles = makeStyles(PendingFilesStyleV2({}));

const NameGetter = versionSelector.bind(this, NameMap);

const MAIN_VIEW = 'pendingFilesCard';

const NotificationIcons = new Map([
  ['GreaterThan24', WarningRoundedIcon]
]);

const IconProps = new Map([
  ['GreaterThan24', {
    fontSize: 'small',
    style: { color: '#F7A40E' }
  }]
]);

const UICollection = new Map([
  ['true-true-true', (props = {}) => <Visits {...props} />],
  ['true-true-false', ({ heading = '', classes = {} }) => <VisitsLoader heading={heading} classes={classes} />],
  ['true-false-true', (props = {}) => <Visits {...props} />],
  ['true-false-false', ({ heading = '', classes = {} }) => <VisitsNoData heading={heading} classes={classes} />],
  ['false-true-true', ({ heading = '', classes = {} }) => <VisitsLoader heading={heading} classes={classes} />],
  ['false-true-false', ({ heading = '', classes = {} }) => <VisitsLoader heading={heading} classes={classes} />],
  ['false-false-true', ({ heading = '', classes = {} }) => <VisitsLoader heading={heading} classes={classes} />],
  ['false-false-false', ({ heading = '', classes = {} }) => <VisitsLoader heading={heading} classes={classes} />],
]);

const VisitsLoader = ({
  heading = '',
  classes,
}) => (
  <div className='flex flex-column flex-fill'>
    <div className='page-header height-56 flex align-items-center justify-content-center p-l-6 p-r-6 border-top-1-light-grey border-bottom-1-light-grey'>
      <p className='page-heading font-proxima-nova font-24 bold'>{heading}</p>
    </div>

    <div className='flex p-l-6 p-r-6'>
      <Table
        classes={classes}
        rows={[]}
        columns={Colums}
        filterBy={''}
        onKeyDown={MockFunction}
        onFilter={MockFunction}
        onRowClick={MockFunction}
        hideSearch
        loading
        loadingRows={LOADING_ROWS}
      />
    </div>
  </div>
);

const VisitsNoData = ({
  heading = '',
  classes,
}) => (
  <div className='flex flex-column flex-fill'>
    <div className='page-header height-56 flex align-items-center justify-content-center p-l-6 p-r-6 border-top-1-light-grey border-bottom-1-light-grey'>
      <p className='page-heading font-proxima-nova font-24 bold'>{heading}</p>
    </div>

    <div className='flex p-l-6 p-r-6'>
      <Table
        classes={classes}
        rows={[]}
        columns={Colums}
        filterBy={''}
        onKeyDown={MockFunction}
        onFilter={MockFunction}
        onRowClick={MockFunction}
        hideSearch
      />
    </div>

    <div className='flex flex-fill justify-content-center p-t-6 p-l-6 p-r-6'>{NO_VISITS_MESSAGE}</div>
  </div>
);

const MockFunction = () => {};

export const VisitsUI = ({
  // action
  onRefresh: handleRefresh = () => {},
  handleRouteChange: goToDetails = () => {},
  setFilterBy = () => {},
  GetPatientMedicalNecessityPDFData: getCmnPdfData = () => {},
  // data
  openView = '',
  asyncOrders = {},
  hideRefresh,
  ...rest
}) => {
  const { filterBy, visits: _visits = {} } = asyncOrders;
  const classes = useStyles();
  const Names = NameGetter(2);

  const visits = useMemo(() => {
    return _visits.data.map((visit) => {
      const item = {
        ...visit,
        Notifications: visit.Notifications || []
      };
      // set date format
      item.AsyncIcon = (
        <div className="page-order-icon">
          <img alt="modality" className='page-order-content-paste-icon' src={ContentPasteIcon} />
        </div>
      );
      item.FullName = `${item.FirstName} ${item.LastName}`;
      item.Notifications = (
        <div className="page-order-notifications">
          {
            item.Notifications.map((icon) => {
              const Icon = NotificationIcons.get(icon);
              const props = IconProps.get(icon);
              return <Icon key={icon} {...props} />;
            })
          }
        </div>
      );
      item.OrderId = `${item.OrderId}`;
      item.key = `${item.OrderId}`;
      return item;
    });
  }, [_visits]);

  const handleKeyDown = useCallback((event) => {
    if (NotAllowedKeysForSearch.has(event.key)) event.preventDefault();
  }, []);

  const handleFilterByChange = useCallback((event) => {
    setFilterBy({ value: event.target.value, target: 'asyncOrders' });
  }, []);

  const handleRowClick = useCallback((row, event) => {
    setFilterBy({ value: '', target: 'asyncOrders' });
    goToDetails(event, MAIN_VIEW, row);
  }, []);

  const UI = useMemo(() => {
    const key = `${asyncOrders?.visits?.called}-${asyncOrders?.visits?.loading}-${asyncOrders?.visits?.data?.length > 0}`;
    if (UICollection.has(key)) {
      return UICollection.get(key);
    }

    return UICollection.get('false-false-false');
  }, [asyncOrders?.visits?.called, asyncOrders?.visits?.loading, asyncOrders?.visits?.data?.length]);

  return (
    <UI
      classes={classes}
      heading={Names.PENDING_FILES_HEADER}
      loading={asyncOrders?.visits?.loading}
      rows={visits}
      columns={Colums}
      filterBy={filterBy}
      onKeyDown={handleKeyDown}
      onFilter={handleFilterByChange}
      onRowClick={handleRowClick}
      onRefresh={handleRefresh}
      hideRefresh={hideRefresh}

      // MainPage props
      {...rest}
      routePath={MAIN_VIEW}
      title={Names.PENDING_FILES_HEADER}
      handleRouteChange={goToDetails}
      data={visits}
      selectedCardData={rest.cardData}
      getCmnPdfData={getCmnPdfData}
    />
  );
};

export default VisitsUI;
